/**
 * 광고·외부 링크 유입(`?ad=<코드>` · utm 5종 · 클릭 id)의 판정·보관 규칙 — 매체를 가리지 않는다.
 *
 * 매체 구분·인정 기간·유효 클릭 판정은 admin 이 코드와 집행 기간으로 한다. 여기는 «받아 적기»만:
 * 첫 유입(`first`)은 한 번 적으면 지키고, 마지막 유입(`last`)은 매번 덮는다. 만료는 두지 않는다.
 * 저장소 접근은 `outreach-ref` 의 감싼 창구를 쓴다 — 실패는 「없음」으로 접는다.
 */

import { readStorageItem, writeStorageItem, type OutreachStorage } from 'src/utils/outreach/outreach-ref';

/** admin 이 발급한 광고 코드를 담는 쿼리 이름 */
export const AD_QUERY_KEY = 'ad';

/** 보관 키(로컬 저장소) — 첫 유입 · 마지막 유입, 각각 `AdTouch` JSON */
export const AD_FIRST_KEY = 'cm.ad.first';
export const AD_LAST_KEY = 'cm.ad.last';

/** 그대로 받아 적는 utm 다섯 */
export const AD_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

/** 이름이 정해진 클릭 id — 네이버 검색광고의 `n_*` 는 접두로 따로 받는다(`isClickIdKey`) */
export const AD_CLICK_ID_KEYS = ['fbclid', 'gclid', 'gbraid', 'wbraid'] as const;

export type AdUtmKey = (typeof AD_UTM_KEYS)[number];

/**
 * 유입 한 번. 없는 값은 `null`(키를 빼지 않는다 — BigQuery JSON 경로가 늘 같다).
 * `click_ids` 는 매체가 늘어도 모양이 안 바뀌는 맵이다. `at` 은 ISO 문자열.
 */
export type AdTouch = { ad: string | null } & Record<AdUtmKey, string | null> & {
    click_ids: Record<string, string>;
    landing_url: string;
    referrer: string | null;
    at: string;
  };

/** admin 발급 광고 코드의 모양인가 */
export function isValidAdCode(value: unknown): value is string {
  return typeof value === 'string' && AD_CODE_PATTERN.test(value);
}

/** 받아 적는 클릭 id 이름인가 — 이름 넷 + 네이버 `n_*` */
export function isClickIdKey(key: string): boolean {
  return (AD_CLICK_ID_KEYS as readonly string[]).includes(key) || NAVER_KEY_PATTERN.test(key);
}

/**
 * 진입 주소에서 유입 한 번을 꺼낸다.
 * @param href     쿼리 포함 원문 주소(`location.href`)
 * @param referrer `document.referrer` — 없으면 빈 문자열
 * @returns 모양 맞는 `ad` 나 클릭 id 가 하나라도 있으면 유입 · 둘 다 없으면 null(utm 만은 유입이 아니다)
 */
export function parseAdTouch(href: string, referrer: string, now: number = Date.now()): AdTouch | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const params = url.searchParams;
  const ad = params.get(AD_QUERY_KEY);
  const clickIds: Record<string, string> = {};
  for (const [key, value] of params) {
    if (Object.keys(clickIds).length >= MAX_CLICK_IDS) break;
    if (isClickIdKey(key) && value) clickIds[key] = clip(value, MAX_CLICK_ID_LENGTH);
  }
  const validAd = isValidAdCode(ad) ? ad : null;
  if (!validAd && Object.keys(clickIds).length === 0) return null;

  return {
    ad: validAd,
    ...utmOf((key) => params.get(key)),
    click_ids: clickIds,
    landing_url: clip(href, MAX_URL_LENGTH),
    referrer: referrer ? clip(referrer, MAX_URL_LENGTH) : null,
    at: new Date(now).toISOString(),
  };
}

/**
 * 밖에서 온 값(저장소 JSON · 가입 보고 본문)을 `AdTouch` 로 좁힌다 — 브라우저와 서버가 같은 판정을 쓴다.
 * @returns 모양이 틀리면 null(길이는 상한으로 자른다)
 */
export function toAdTouch(value: unknown): AdTouch | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (v.ad !== null && !isValidAdCode(v.ad)) return null;
  if (typeof v.landing_url !== 'string' || typeof v.at !== 'string' || Number.isNaN(Date.parse(v.at))) return null;
  if (v.referrer !== null && typeof v.referrer !== 'string') return null;
  if (!v.click_ids || typeof v.click_ids !== 'object' || Array.isArray(v.click_ids)) return null;

  const clickIds: Record<string, string> = {};
  for (const [key, id] of Object.entries(v.click_ids as Record<string, unknown>)) {
    if (Object.keys(clickIds).length >= MAX_CLICK_IDS) break;
    if (isClickIdKey(key) && typeof id === 'string' && id) clickIds[key] = clip(id, MAX_CLICK_ID_LENGTH);
  }
  return {
    ad: v.ad as string | null,
    ...utmOf((key) => (typeof v[key] === 'string' ? (v[key] as string) : null)),
    click_ids: clickIds,
    landing_url: clip(v.landing_url, MAX_URL_LENGTH),
    referrer: typeof v.referrer === 'string' ? clip(v.referrer, MAX_URL_LENGTH) : null,
    at: v.at,
  };
}

/** 보관된 유입을 읽는다 — 없거나 모양이 틀리면 null */
export function readAdTouch(storage: OutreachStorage, which: 'first' | 'last'): AdTouch | null {
  const raw = readStorageItem(storage, which === 'first' ? AD_FIRST_KEY : AD_LAST_KEY);
  if (!raw) return null;
  try {
    return toAdTouch(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * 유입을 보관한다 — 첫 유입은 **살아 있는 보관본이 있으면 지키고**, 마지막 유입은 늘 덮는다.
 * @returns `isFirstTouch` — 이번 유입이 첫 유입으로 적혔나
 */
export function storeAdTouch(storage: OutreachStorage, touch: AdTouch): { isFirstTouch: boolean } {
  const json = JSON.stringify(touch);
  const isFirstTouch = !readAdTouch(storage, 'first');
  if (isFirstTouch) writeStorageItem(storage, AD_FIRST_KEY, json);
  writeStorageItem(storage, AD_LAST_KEY, json);
  return { isFirstTouch };
}

/** utm 다섯을 꺼낸다 — 없는 값은 null, 긴 값은 상한으로 자른다. 메일 유입(`outreach-touch`)도 같은 규칙을 쓴다 */
export function utmOf(get: (key: AdUtmKey) => string | null): Record<AdUtmKey, string | null> {
  const out = {} as Record<AdUtmKey, string | null>;
  for (const key of AD_UTM_KEYS) {
    const value = get(key);
    out[key] = value ? clip(value, MAX_UTM_LENGTH) : null;
  }
  return out;
}

/** 문자열을 상한 길이로 자른다 */
export function clip(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

/** 주소·referrer 상한 */
export const MAX_URL_LENGTH = 2048;

// ---- 사적 부품 ---------------------------------------------------------

const AD_CODE_PATTERN = /^[A-Za-z0-9_-]{4,32}$/;
/** 네이버 검색광고 자동 추적 파라미터 — `n_media` · `n_query` · `n_keyword_id` … */
const NAVER_KEY_PATTERN = /^n_[A-Za-z0-9_]{1,40}$/;
const MAX_CLICK_IDS = 30;
const MAX_CLICK_ID_LENGTH = 512;
const MAX_UTM_LENGTH = 256;

